import Form, { Field, FormFooter, HelperMessage } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';
import { ControlType } from 'core/enums/control-type';
import { AlertTriangle } from 'react-feather';
import type { ValueType } from '@atlaskit/select/types';
import { SelectOption } from 'core/utilities/interface-helpers';
import Select, { CreatableSelect } from '@atlaskit/select';
import { ButtonGroup } from '@atlaskit/button';
import SharedButton from '../buttons/button';
import { ISharedForm, ISharedFormField } from './form-interface';
import { DatePicker, TimePicker } from '@atlaskit/datetime-picker';
import TextArea from '@atlaskit/textarea';
import AddressSearch from './address-search/address-search';
import ImageCapture from './image-capture/image-capture';
import { IAddEditDeliveryFormAddress } from 'modules/deliveries/add-edit-delivery-dialog/add-edit-delivery-dialog-interface';
import SkeletonElement from '../layout/skeleton-element';
import { DaysOfWeek } from 'core/constants/days-of-week';
import DayOfTheWeekPicker from './day-of-the-week/day-of-the-week-picker';

const SharedForm = ({
  onSubmit,
  onChange,
  fields,
  buttonLabel,
  loading,
  formErrorMessage,
  cancelButton,
  initialising = false,
  className = '',
}: React.PropsWithChildren<ISharedForm>) => {
  const validateField = (field: ISharedFormField, value?: any) => {
    if (!value && field.required) {
      return 'REQUIRED';
    }

    if (value.valid === false) {
      return 'INVALID';
    }
  };

  const getFormField = (field: ISharedFormField) => {
    switch (field.control) {
      case ControlType.Select:
        return (
          <Field<ValueType<SelectOption>>
            key={field.key}
            aria-required={field.required}
            name={field.key}
            defaultValue={(field.defaultValue as SelectOption) ?? null}
            label={field.label}
            isRequired={field.required}
            isDisabled={loading || field.isDisabled}
            validate={async (value) => {
              if (field.required && !value) {
                return 'REQUIRED';
              }
            }}
          >
            {({ fieldProps, error }) => (
              <>
                <Select<SelectOption>
                  {...fieldProps}
                  options={field.options}
                  onChange={(value) => {
                    fieldProps.onChange(value);
                    onChange?.({ [field.key]: value });
                  }}
                  validationState={error ? 'error' : 'default'}
                  isSearchable={true}
                />
                {error === 'REQUIRED' && <p className='mt-2 label-01 text-red-600'>This field is required</p>}
              </>
            )}
          </Field>
        );
      case ControlType.SelectMulti:
        return (
          <Field<ValueType<SelectOption, true>>
            key={field.key}
            aria-required={field.required}
            name={field.key}
            defaultValue={(field.defaultValue as SelectOption[]) ?? []}
            label={field.label}
            isRequired={field.required}
            isDisabled={loading || field.isDisabled}
            validate={(value) => validateField(field, value)}
          >
            {({ fieldProps, error }) => (
              <>
                <Select<SelectOption, true>
                  {...fieldProps}
                  options={field.options}
                  isMulti
                  onChange={(value) => {
                    fieldProps.onChange(value);
                    onChange?.({ [field.key]: value });
                  }}
                  validationState={error ? 'error' : 'default'}
                  isSearchable={true}
                />
                {field.helperMessage && <HelperMessage>{field.helperMessage}</HelperMessage>}
                {error === 'REQUIRED' && <p className='mt-2 label-01 text-red-600'>This field is required</p>}
              </>
            )}
          </Field>
        );
      case ControlType.SelectCreate:
        return (
          <Field<ValueType<SelectOption, true>>
            key={field.key}
            aria-required={field.required}
            name={field.key}
            defaultValue={(field.defaultValue as SelectOption[]) ?? []}
            label={field.label}
            isRequired={field.required}
            isDisabled={loading || field.isDisabled}
            validate={(value) => validateField(field, value)}
          >
            {({ fieldProps, error }) => (
              <>
                <CreatableSelect<SelectOption, true>
                  {...fieldProps}
                  options={field.options}
                  isMulti
                  placeholder={field.placeholder}
                  onChange={(value) => {
                    fieldProps.onChange(value);
                    onChange?.({ [field.key]: value });
                  }}
                  onCreateOption={(inputValue) => {
                    const newOption: SelectOption = {
                      label: inputValue,
                      value: inputValue.toLowerCase().replace(/\W/g, ''),
                    };

                    const currentValue = (fieldProps.value as SelectOption[]) || [];
                    fieldProps.onChange([...currentValue, newOption]);
                    onChange?.({ [field.key]: [...currentValue, newOption] });
                  }}
                  validationState={error ? 'error' : 'default'}
                  isSearchable={true}
                />
                {field.helperMessage && <HelperMessage>{field.helperMessage}</HelperMessage>}
                {error === 'REQUIRED' && <p className='mt-2 label-01 text-red-600'>This field is required</p>}
              </>
            )}
          </Field>
        );
      case ControlType.DatePicker:
        return (
          <Field
            key={field.key}
            name={field.key}
            label={field.label}
            defaultValue={(field.defaultValue as string) ?? ''}
            isRequired={field.required}
            isDisabled={loading || field.isDisabled}
            validate={(value) => validateField(field, value)}
          >
            {({ fieldProps: { id, ...rest }, error }) => (
              <>
                <DatePicker
                  id={id}
                  {...rest}
                  locale={'en-UK'}
                  minDate={field.minDate}
                  onChange={(value) => {
                    rest.onChange(value);
                    onChange?.({ [field.key]: value });
                  }}
                />
                {field.helperMessage && <HelperMessage>{field.helperMessage}</HelperMessage>}
                {error === 'REQUIRED' && <p className='mt-2 label-01 text-red-600'>This field is required</p>}
              </>
            )}
          </Field>
        );
      case ControlType.TimePicker:
        return (
          <Field
            key={field.key}
            name={field.key}
            label={field.label}
            defaultValue={(field.defaultValue as string) ?? ''}
            isRequired={field.required}
            isDisabled={loading || field.isDisabled}
            validate={(value) => validateField(field, value)}
          >
            {({ fieldProps: { id, ...rest }, error }) => (
              <>
                <TimePicker
                  id={id}
                  {...rest}
                  locale={'en-UK'}
                  timeFormat='HH:mm'
                  {...(field.times && { times: field.times })}
                  onChange={(value) => {
                    rest.onChange(value);
                    onChange?.({ [field.key]: value });
                  }}
                />
                {field.helperMessage && <HelperMessage>{field.helperMessage}</HelperMessage>}
                {error === 'REQUIRED' && <p className='mt-2 label-01 text-red-600'>This field is required</p>}
              </>
            )}
          </Field>
        );
      case ControlType.TextArea:
        return (
          <Field
            key={field.key}
            name={field.key}
            label={field.label}
            defaultValue={field.defaultValue ?? ''}
            aria-required={field.required}
            isRequired={field.required}
            isDisabled={loading || field.isDisabled}
          >
            {({ fieldProps }: any) => (
              <>
                <TextArea
                  {...fieldProps}
                  onChange={(value) => {
                    fieldProps.onChange(value);
                    onChange?.({ [field.key]: value });
                  }}
                />
                {field.helperMessage && <HelperMessage>{field.helperMessage}</HelperMessage>}
              </>
            )}
          </Field>
        );
      case ControlType.AddressSearch:
        return (
          <Field
            key={field.key}
            aria-required={field.required}
            name={field.key}
            label={field.label}
            isRequired={field.required}
            validate={(value) => validateField(field, value)}
            isDisabled={loading || field.isDisabled}
            defaultValue={(field.defaultValue as IAddEditDeliveryFormAddress) ?? ''}
          >
            {({ fieldProps, error }) => (
              <>
                <AddressSearch fieldProps={fieldProps} />
                {field.helperMessage && <HelperMessage>{field.helperMessage}</HelperMessage>}
                {error === 'INVALID' && (
                  <p className='mt-2 label-01 text-red-600'>Please select an address from the list of suggestions</p>
                )}
              </>
            )}
          </Field>
        );
      case ControlType.ImageCapture:
        return (
          <Field
            key={field.key}
            aria-required={field.required}
            name={field.key}
            label={field.label}
            isRequired={field.required}
            validate={(value) => validateField(field, value)}
            isDisabled={loading || field.isDisabled}
          >
            {({ fieldProps, error }) => (
              <>
                <ImageCapture fieldProps={fieldProps} />
                {field.helperMessage && <HelperMessage>{field.helperMessage}</HelperMessage>}
                {error === 'INVALID' && (
                  <p className='mt-2 label-01 text-red-600'>Please select an address from the list of suggestions</p>
                )}
              </>
            )}
          </Field>
        );
      case ControlType.Switch:
        return (
          <Field
            key={field.key}
            aria-required={field.required}
            name={field.key}
            defaultValue={(field.defaultValue as boolean) ?? false}
            label={field.label}
            isDisabled={loading || field.isDisabled}
          >
            {({ fieldProps: { value, onChange, ...rest } }) => (
              <Toggle
                {...rest}
                isChecked={value}
                onChange={onChange}
                defaultChecked={(field.defaultValue as boolean) ?? false}
                size='large'
              />
            )}
          </Field>
        );
      case ControlType.DayOfTheWeekPicker:
        return (
          <Field
            key={field.key}
            aria-required={field.required}
            name={field.key}
            defaultValue={(field.defaultValue as DaysOfWeek[]) ?? []}
            label={field.label}
            isDisabled={loading || field.isDisabled}
          >
            {({ fieldProps }) => <DayOfTheWeekPicker fieldProps={fieldProps} />}
          </Field>
        );
      default:
        return (
          <Field
            key={field.key}
            aria-required={field.required}
            name={field.key}
            defaultValue={(field.defaultValue as string) ?? ''}
            label={field.label}
            isRequired={field.required}
            isDisabled={loading || field.isDisabled}
          >
            {({ fieldProps }) => (
              <>
                <TextField {...fieldProps} type={field.type} />
                {field.helperMessage && <HelperMessage>{field.helperMessage}</HelperMessage>}
              </>
            )}
          </Field>
        );
    }
  };

  return (
    <Form onSubmit={(data) => onSubmit(data)}>
      {({ formProps }) => (
        <form {...formProps} className={className}>
          {fields
            .filter((field) => !field.hidden)
            .map((field) =>
              initialising ? (
                <div className='mt-[16px]'>
                  <SkeletonElement height='18px' width='100px' className='mb-[4px]' />
                  <SkeletonElement height='36px' width='100%' />
                  <div />
                </div>
              ) : (
                getFormField(field)
              )
            )}
          {formErrorMessage && (
            <div className='mt-6 label-01 text-red-600 flex items-center'>
              <AlertTriangle className='mr-3' />
              {formErrorMessage}
            </div>
          )}
          <FormFooter>
            <ButtonGroup>
              {cancelButton && <SharedButton {...cancelButton} />}
              <SharedButton
                onClick={() => undefined}
                type='submit'
                appearance='primary'
                isLoading={loading}
                label={buttonLabel}
              />
            </ButtonGroup>
          </FormFooter>
        </form>
      )}
    </Form>
  );
};

export default SharedForm;
